/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';

import { useAction } from '../../../Common/hooks';
import { oneSecond } from '../../../Common/constants';
import { getVisitFilterOptions, getAgencies } from '../../services';
import { setVisitsFilters, fetchVisitsRecords, cleanVisitsRecords } from '../../actions';
import { selectVisitsFilters } from '../../selectors';
import { visitsFiltersInitialState } from '../../reducers';

const useVisitFilterBar = () => {
  const storeFilters = useSelector(selectVisitsFilters);
  const setStoreFilters = useAction(setVisitsFilters);
  const dispatchFetchVisitsRecords = useAction(fetchVisitsRecords);
  const dispatchCleanVisitsRecords = useAction(cleanVisitsRecords);
  const debouncedFilterChange = useCallback(debounce(setStoreFilters, oneSecond), []);
  const [partnersOptions, setPartnersOptions] = useState([]);
  const [aggregatorsOptions, setAggregatorsOptions] = useState([]);
  const [agencyOptions, setAgencyOptions] = useState([]);
  const [disableResetButton, setDisableResetButton] = useState(false);
  const [disableApplyButton, setDisableApplyButton] = useState(false);
  const [isInputFilter, setIsInputFilter] = useState(false);
  const [filters, setFilters] = useState({ ...storeFilters });

  useEffect(() => {
    if (isInputFilter) {
      debouncedFilterChange(filters);
      setIsInputFilter(false);
    } else {
      setStoreFilters(filters);
    }
    setDisableResetButton(isEqual(filters, visitsFiltersInitialState));
    setDisableApplyButton(isEqual(filters, visitsFiltersInitialState));
  }, [filters]);

  useEffect(() => {
    const callFilterOptions = async () => {
      const filterOptions = await getVisitFilterOptions();
      const [partnersFilterOptions, aggregatorsFilterOptions, agenciesOptions] = filterOptions;

      setPartnersOptions(partnersFilterOptions);
      setAggregatorsOptions(aggregatorsFilterOptions);
      setAgencyOptions(agenciesOptions);
    };

    callFilterOptions();
  }, []);

  const updateAgencyFilterOptions = async value => {
    const requestParams = {
      active: true,
      partnerID: value,
    };

    const agencyOptionsPerPartner = await getAgencies({ filters: requestParams });
    setAgencyOptions(agencyOptionsPerPartner);
  };

  const handleFilterChanges =
    filterName =>
    ({ target: { type, value } }) => {
      const filter = { [filterName]: value };

      if (type === 'text') {
        setIsInputFilter(true);
      }

      if (filterName === 'partnerID') {
        updateAgencyFilterOptions(value);
      }

      setFilters({
        ...filters,
        ...filter,
        pageNumber: 1,
        pageSize: storeFilters.pageSize,
      });
    };

  const handleResetFilter = () => {
    setFilters({ ...visitsFiltersInitialState });
    setDisableApplyButton(true);
    dispatchCleanVisitsRecords();
  };

  const handleEnterPress = event => {
    if (event.key === 'Enter') {
      debouncedFilterChange.flush();
    }
  };

  const handleApply = () => {
    dispatchFetchVisitsRecords();
  };

  return {
    partnersOptions,
    aggregatorsOptions,
    agencyOptions,
    disableResetButton,
    handleFilterChanges,
    handleResetFilter,
    handleEnterPress,
    filters,
    handleApply,
    disableApplyButton,
  };
};

export default useVisitFilterBar;
